import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pie } from "react-chartjs-2";
import PrimeTable from "../PrimeTable/PrimeTable";
import func from "../../services/functions";
import ReportExportBar from "./ReportExportBar";
import "chart.js/auto";

const SummaryCard = ({ label, value, variant = "primary" }) => (
  <div className={`reports__summary-card reports__summary-card--${variant}`}>
    <div className="reports__summary-card-icon" />
    <div className="reports__summary-card-content">
      <span className="reports__summary-card-label">{label}</span>
      <span className="reports__summary-card-value">{value}</span>
    </div>
  </div>
);

const ReportContent = ({ data, config, dateRange, companyName, loading }) => {
  const { t } = useTranslation("translations");
  const [selectedColumns, setSelectedColumns] = useState(null);

  // Translate columns and format for PrimeTable
  const translatedColumns = useMemo(() => {
    if (!config?.columns) return [];
    return config.columns.map((col, index) => ({
      header: t(col.headerKey),
      field: col.field,
      isMoney: col.isMoney,
      sortable: true,
      filter: true,
      default: true,
      style: {
        minWidth: col.isMoney ? "180px" : "150px",
      },
      align: col.isMoney ? "right" : "left",
      alignHeader: col.isMoney ? "right" : "left",
      body: col.isMoney
        ? (row) => row[col.field] != null
          ? func.getFormattedMoneyValue(row[col.field]) + " L"
          : ""
        : undefined,
      ...(col.mobile ? { mobile: col.mobile } : {}),
    }));
  }, [config, t]);

  // Initialize selectedColumns when config changes
  useEffect(() => {
    if (config?.columns) {
      setSelectedColumns(
        config.columns.map((col) => ({
          field: col.field,
          show: true,
        }))
      );
    }
  }, [config]);

  // Get searchable keys from columns
  const searchableKeys = useMemo(() => {
    if (!config?.columns) return [];
    return config.columns.map((col) => col.field);
  }, [config]);

  // All hooks must be called before any early returns
  const summaryValues = useMemo(() => {
    if (!data || !config?.summaryFields || data.length === 0) return [];
    return config.summaryFields.map((field) => {
      let value;
      if (field.key === "_count") {
        value = data.length;
      } else if (field.aggregate === "sum") {
        value = data.reduce((sum, row) => sum + (row[field.key] || 0), 0);
      } else {
        value = data.reduce((sum, row) => sum + (row[field.key] || 0), 0);
      }
      return {
        ...field,
        label: t(field.labelKey),
        displayValue: field.isMoney
          ? func.getFormattedMoneyValue(value) + " L"
          : value.toString(),
      };
    });
  }, [data, config, t]);

  const chartData = useMemo(() => {
    if (!data || !config?.chartFields || data.length === 0) return null;
    const { labels: labelField, datasets: dsConfigs } = config.chartFields;

    const colors = ["#007567", "#3b82f6", "#6366f1", "#f59e0b", "#ef4444", "#10b981", "#8b5cf6", "#ec4899"];

    if (dsConfigs.length === 1) {
      // Single dataset: each row's label field becomes a slice (category breakdown)
      const aggregated = {};
      data.forEach((item) => {
        const label = item[labelField] ?? "";
        if (!aggregated[label]) aggregated[label] = 0;
        aggregated[label] += item[dsConfigs[0].key] || 0;
      });
      const chartLabels = Object.keys(aggregated);
      const values = Object.values(aggregated);
      return {
        labels: chartLabels,
        datasets: [{
          data: values,
          backgroundColor: chartLabels.map((_, i) => colors[i % colors.length]),
          borderColor: "#fff",
          borderWidth: 1,
        }],
      };
    }

    // Multiple datasets: aggregate each dataset key into a single slice (Option A)
    const totals = dsConfigs.map((ds) =>
      data.reduce((sum, row) => sum + (row[ds.key] || 0), 0)
    );
    return {
      labels: dsConfigs.map((ds) => t(ds.labelKey)),
      datasets: [{
        data: totals,
        backgroundColor: dsConfigs.map((ds) => ds.color + "CC"),
        borderColor: dsConfigs.map((ds) => ds.color),
        borderWidth: 1,
      }],
    };
  }, [data, config, t]);

  if (loading) {
    return (
      <div className="reports__content">
        <div className="reports__loading">
          <div className="reports__loading-spinner" />
          <p>{t("reports.filters.loadingData")}</p>
        </div>
      </div>
    );
  }

  if (!data || !config) return null;

  if (data.length === 0) {
    return (
      <div className="reports__content">
        <div className="reports__empty">
          <p>{t("reports.filters.noDataFound")}</p>
        </div>
      </div>
    );
  }

  const reportTitle = t(config.titleKey);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right" },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = func.getFormattedMoneyValue(ctx.raw);
            return `${ctx.label}: ${value} L`;
          },
        },
      },
    },
  };

  return (
    <div className="reports__content">
      {/* Summary Cards */}
      <div className="reports__summary">
        {summaryValues.map((sv) => (
          <SummaryCard
            key={sv.key}
            label={sv.label}
            value={sv.displayValue}
            variant={sv.variant}
          />
        ))}
      </div>

      {/* Export Bar */}
      <ReportExportBar
        data={data}
        columns={translatedColumns}
        reportTitle={reportTitle}
        dateRange={dateRange}
        companyName={companyName}
      />

      {/* Data Table */}
      <div className="reports__table">
        <PrimeTable
          columns={translatedColumns}
          value={data}
          simpleTable={true}
          selectedColumns={[selectedColumns, setSelectedColumns]}
          showSelectColumns={true}
          searchableKeys={searchableKeys}
          showGlobalSearch={true}
        />
      </div>

      {/* Chart */}
      {chartData && data.length <= 50 && (
        <div className="reports__chart">
          <h3 className="reports__chart-title">{reportTitle} - {t("reports.filters.chart")}</h3>
          <div className="reports__chart-container">
            <Pie data={chartData} options={chartOptions} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportContent;
